import React, { useEffect, useMemo, useState } from "react";
import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  PDFViewer,
  Image,
  PDFDownloadLink,
  Svg,
  Line,
} from "@react-pdf/renderer";
import { Request } from "../../hooks/Request";
import { requestAuth } from "../services/RequestService";
import { Button } from "../Buttons";
import Circle from "../Animation/Circle";
import {
  DEFAULT_PROVIDER_PURCHASE_ORDER_DOCUMENT_CHECKS,
  PROVIDER_PURCHASE_ORDER_DOCUMENTS,
} from "../../utils/providerPurchaseOrderDocuments";

const PENDING_VALUE = "?";
const EMPTY_VALUE = " ";
const LOGO_PATH = "/images/logo-Plastiforte.png";

const PURCHASE_ORDER_PDF_TYPES = {
  INTERNAL: "internal",
  PROVIDER: "provider",
};

const LANGUAGES = { ES: "es", EN: "en" };

const translations = {
  es: {
    lang: "es",
    purchaseOrderTitle: "ORDEN DE COMPRA",
    purchaseOrderSubtitle: "Compras Importaciones",
    page: "Página",
    of: "de",
    date: "FECHA :",
    ocNumber: "Nro. O.C.:",
    scNumber: "SC No. :",
    orderCode: "Pedido :",
    plastiforteShort: "Plastiforte",
    plastiforteLong: "Plastiforte SRL",
    att: "Att :",
    nit: "NIT :",
    phone: "Telefono :",
    fax: "Fax :",
    email: "E-mail :",
    provider: "Proveedor :",
    providerContact: "Att. :",
    quoteNumber: "N° Cotización:",
    address: "Dirección:",
    for: "Para :",
    phoneShort: "Telf. :",
    paymentTerms: "TERMINOS DE PAGO :",
    incoterms: "INCOTERMS :",
    conditions: "CONDICIONES :",
    observations: "OBSERVACIONES :",
    item: "Item",
    description: "Descripción - Observación",
    unit: "Unidad",
    quantity: "Cantidad",
    unitPrice: "Precio Unitario",
    discount: "Descuento",
    total: "Total",
    deliveryDate: "Fecha Entrega",
    subtotal: "Subtotal",
    totalLabel: "TOTAL",
    purchaseOrderIn: "ORDEN DE COMPRA EN",
    documentsRequest:
      "Se solicita adjuntar los documentos originales detallados a continuación:",
    note: "Nota: ",
    noteText:
      "Queda prohibido el envío de muestras, obsequios, material promocional u otros artículos no incluidos en la presente Orden de Compra sin autorización previa del Comprador y sin su correspondiente declaración en la Factura Comercial.",
    approvedBy: "Aprobado por",
    providerApproval: "Conformidad Proveedor",
    pdfInternal: "PDF interno",
    pdfProvider: "PDF proveedor",
    changeDocuments: "Cambiar documentos",
    downloadProviderPdf: "Descargar PDF Proveedor",
    downloadOrder: "Descargar Orden",
    generating: "Generando...",
    loadingData: "Cargando datos...",
    syncing: "Sincronizando con el servidor...",
    completingProvider: "Completando datos del proveedor...",
    providerNotFound:
      "No se encontró detalle del proveedor. Se mostrarán campos pendientes.",
    documentsChecked: "documento(s) marcados",
    orderOf: "Orden de Compra",
    nro: "Nro.",
    pdfSuffixInternal: "Interna",
    pdfSuffixProvider: "Proveedor",
    codigo: "CODIGO:",
    version: "VERSION:",
    cc: "C.C. :",
    ca: "C.A. :",
    docsModalTitle: "Documentos para PDF proveedor",
    docsModalDescription:
      "Marca los documentos que deben solicitarse en la Orden de Compra.",
    selectAll: "Marcar todos",
    deselectAll: "Desmarcar todos",
    clear: "Limpiar",
    cancel: "Cancelar",
    generateProviderPdf: "Generar PDF proveedor",
    printInEnglish: "Imprimir en inglés",
    printInSpanish: "Imprimir en español",
    providerFormat: "FORMATO PROVEEDOR",
  },
  en: {
    lang: "en",
    purchaseOrderTitle: "PURCHASE ORDER",
    purchaseOrderSubtitle: "Import Purchases",
    page: "Page",
    of: "of",
    date: "DATE :",
    ocNumber: "OC No.:",
    scNumber: "SC No.:",
    orderCode: "Order :",
    plastiforteShort: "Plastiforte",
    plastiforteLong: "Plastiforte SRL",
    att: "Att :",
    nit: "NIT :",
    phone: "Phone :",
    fax: "Fax :",
    email: "E-mail :",
    provider: "Provider :",
    providerContact: "Att. :",
    quoteNumber: "Quote No.:",
    address: "Address:",
    for: "For :",
    phoneShort: "Tel. :",
    paymentTerms: "PAYMENT TERMS :",
    incoterms: "INCOTERMS :",
    conditions: "CONDITIONS :",
    observations: "OBSERVATIONS :",
    item: "Item",
    description: "Description - Remarks",
    unit: "Unit",
    quantity: "Quantity",
    unitPrice: "Unit Price",
    discount: "Discount",
    total: "Total",
    deliveryDate: "Delivery Date",
    subtotal: "Subtotal",
    totalLabel: "TOTAL",
    purchaseOrderIn: "PURCHASE ORDER IN",
    documentsRequest:
      "You are requested to attach the original documents detailed below:",
    note: "Note: ",
    noteText:
      "The sending of samples, gifts, promotional material or other articles not included in this Purchase Order is prohibited without prior authorization from the Buyer and without their corresponding declaration on the Commercial Invoice.",
    approvedBy: "Approved by",
    providerApproval: "Provider Conformity",
    pdfInternal: "Internal PDF",
    pdfProvider: "Provider PDF",
    changeDocuments: "Change documents",
    downloadProviderPdf: "Download Provider PDF",
    downloadOrder: "Download Order",
    generating: "Generating...",
    loadingData: "Loading data...",
    syncing: "Syncing with server...",
    completingProvider: "Completing provider data...",
    providerNotFound:
      "Provider details not found. Pending fields will be shown.",
    documentsChecked: "document(s) checked",
    orderOf: "Purchase Order",
    nro: "No.",
    pdfSuffixInternal: "Internal",
    pdfSuffixProvider: "Provider",
    codigo: "CODE:",
    version: "VERSION:",
    cc: "C.C. :",
    ca: "C.A. :",
    docsModalTitle: "Documents for Provider PDF",
    docsModalDescription:
      "Check the documents to be requested in the Purchase Order.",
    selectAll: "Select all",
    deselectAll: "Deselect all",
    clear: "Clear",
    cancel: "Cancel",
    generateProviderPdf: "Generate Provider PDF",
    printInEnglish: "Print in English",
    printInSpanish: "Print in Spanish",
    providerFormat: "PROVIDER FORMAT",
  },
};

const normalizeProviderDocumentChecks = (checks = {}) =>
  PROVIDER_PURCHASE_ORDER_DOCUMENTS.reduce((acc, item) => {
    acc[item.key] = Boolean(checks?.[item.key]);
    return acc;
  }, {});

const LIST_FIELDS = [
  "fechaDeOrdenDeCompra",
  "nroOc",
  "scNro",
  "codigoDeInterno",
  "proveedor",
  "terminosPago",
  "condiciones",
  "obsDeLaOrdenDeCompra",
  "adicionalesDeLaOrdenDeCompra",
  "subtotalOrdenDeCompra",
  "totalOrdenDeCompra",
  "moneda",
  "itemsOrdenDeCompra",
];

const hasValue = (value) =>
  value !== null && value !== undefined && value !== "";

const cleanText = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const PROVIDER_LEGAL_SUFFIXES = [
  "JOINT STOCK COMPANY",
  "GMBH & CO",
  "CO LTDA",
  "CO LTD",
  "CORPORATION",
  "LIMITADA",
  "LIMITED",
  "LTDA",
  "GMBH",
  "CORP",
  "EIRL",
  "LLC",
  "INC",
  "LTD",
  "SRL",
  "SAC",
  "SAS",
  "SPA",
  "SDN",
  "BHD",
  "IND",
  "SA",
  "SL",
  "AG",
  "NV",
  "AS",
  "CO",
];

const normalizeProviderName = (value) =>
  cleanText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.,]/g, "")
    .replace(/[^a-zA-Z0-9&]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

const stripProviderLegalSuffix = (normalizedName) => {
  const suffix = PROVIDER_LEGAL_SUFFIXES.find((legalSuffix) =>
    normalizedName.endsWith(` ${legalSuffix}`),
  );

  return suffix ? normalizedName.slice(0, -(suffix.length + 1)).trim() : null;
};

const getProviderBaseName = (value) => {
  const normalizedName = normalizeProviderName(value);
  const nameWithoutTrailingAlias = normalizeProviderName(
    cleanText(value).replace(/\s*\([^)]*\)\s*$/, ""),
  );

  return (
    stripProviderLegalSuffix(normalizedName) ||
    stripProviderLegalSuffix(nameWithoutTrailingAlias) ||
    normalizedName
  );
};

const getProviderSearchTerms = (providerName) => {
  const original = cleanText(providerName);
  const baseName = getProviderBaseName(original);
  const firstWord = baseName.split(" ")[0];

  return Array.from(new Set([original, baseName, firstWord].filter(Boolean)));
};

const buildFieldQuery = () =>
  LIST_FIELDS.map(
    (fieldName) => `fieldnames=${encodeURIComponent(fieldName)}`,
  ).join("&");

const safeJson = (value) => {
  if (!hasValue(value)) return {};
  if (typeof value === "object") return value;

  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const objectText = (value) => {
  if (!value || typeof value !== "object") return "";

  return (
    value.label ||
    value.Label ||
    value.name ||
    value.Name ||
    value.razonSocial ||
    value.RazonSocial ||
    value.value ||
    value.Value ||
    value.description ||
    value.Description ||
    ""
  );
};

const display = (value, fallback = EMPTY_VALUE) => {
  if (!hasValue(value)) return fallback;

  if (typeof value === "object") {
    const result = cleanText(objectText(value));
    return result || fallback;
  }

  const result = cleanText(value);
  return result || fallback;
};

const required = (value) => display(value, PENDING_VALUE);

const optional = (value) => display(value, EMPTY_VALUE);

const upper = (value, fallback = EMPTY_VALUE) =>
  display(value, fallback).toUpperCase();

const normalizeSearchText = (value) =>
  cleanText(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.,:;_/-]/g, " ")
    .replace(/\s+/g, " ")
    .toUpperCase();

const isExchangeRateDifference = (additional) => {
  const label = normalizeSearchText(additional?.label);

  if (!label) return false;

  return (
    label.includes("TIPO DE CAMBIO") ||
    label.includes("TIPO CAMBIO") ||
    label.includes("DIFERENCIA CAMBIARIA") ||
    label.includes("DIFERENCIA DE CAMBIO") ||
    label.includes("AJUSTE CAMBIARIO") ||
    label.includes("AJUSTE DE CAMBIO") ||
    /(^| )TC( |$)/.test(label) ||
    /(^| )T C( |$)/.test(label)
  );
};

const toNumber = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (!hasValue(value)) return null;

  const raw = String(value)
    .trim()
    .replace(/\s/g, "")
    .replace(/[^0-9,.-]/g, "");

  if (!raw) return null;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");

  let normalized = raw;

  if (lastComma !== -1 && lastDot !== -1) {
    normalized =
      lastComma > lastDot
        ? raw.replace(/\./g, "").replace(",", ".")
        : raw.replace(/,/g, "");
  } else if (lastComma !== -1) {
    normalized = raw.replace(",", ".");
  }

  const numberValue = Number(normalized);
  return Number.isFinite(numberValue) ? numberValue : null;
};

const money = (value, fallback = EMPTY_VALUE) => {
  const numberValue = toNumber(value);
  if (numberValue === null) return fallback;

  return numberValue.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value, fallback = PENDING_VALUE) => {
  if (!hasValue(value)) return fallback;

  const raw = String(value).trim();
  if (!raw) return fallback;

  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day}/${month}/${year}`;
  }

  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (slashMatch) return raw;

  const parsedDate = new Date(raw);

  if (!Number.isNaN(parsedDate.getTime())) {
    return parsedDate.toLocaleDateString("es-BO");
  }

  return raw;
};

const getArray = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.records)) return payload.records;
  return [];
};

const normalizeProviderOption = (providerOption) => {
  if (!providerOption || typeof providerOption !== "object") return null;

  const details = safeJson(providerOption.data || providerOption.Data);

  return {
    ...providerOption,
    ...details,
    name:
      providerOption.name ||
      providerOption.Name ||
      details.name ||
      details.nombre ||
      "",
    rawData: details,
  };
};

const findMatchingProvider = (providerOptions, providerName) => {
  const normalizedProviderOptions = providerOptions
    .map(normalizeProviderOption)
    .filter(Boolean);

  if (!normalizedProviderOptions.length) return null;

  const normalizedProviderName = normalizeProviderName(providerName);
  const exactMatches = normalizedProviderOptions.filter(
    (provider) =>
      normalizeProviderName(provider.name) === normalizedProviderName,
  );

  if (exactMatches.length === 1) return exactMatches[0];
  if (exactMatches.length > 1) return null;

  const providerBaseName = getProviderBaseName(providerName);
  const baseMatches = normalizedProviderOptions.filter(
    (provider) => getProviderBaseName(provider.name) === providerBaseName,
  );

  return baseMatches.length === 1 ? baseMatches[0] : null;
};

const getPurchaseOrderProviderName = (data) => display(data?.proveedor, "");

const getProviderContact = (provider) => {
  const contacts =
    provider?.contactos || provider?.contacts || provider?.contact;

  if (!hasValue(contacts)) return PENDING_VALUE;
  if (typeof contacts === "string") return contacts.trim() || PENDING_VALUE;

  const firstContact = Array.isArray(contacts)
    ? contacts.find(Boolean)
    : contacts;

  if (!firstContact) return PENDING_VALUE;
  if (typeof firstContact === "string")
    return firstContact.trim() || PENDING_VALUE;

  const name =
    firstContact.nombre ||
    firstContact.name ||
    firstContact.fullName ||
    firstContact.label ||
    "";

  const position =
    firstContact.cargo || firstContact.position || firstContact.puesto || "";

  return [name, position].filter(Boolean).join(" - ") || PENDING_VALUE;
};

const providerLocation = (provider) => {
  const country = optional(provider?.pais || provider?.country);
  const city = optional(provider?.ciudad || provider?.city);

  const values = [country, city].filter(
    (value) => value !== EMPTY_VALUE && value !== PENDING_VALUE,
  );

  return values.length ? values.join(" - ").toUpperCase() : EMPTY_VALUE;
};

const getCurrencyLabel = (currency) => {
  const value = upper(currency, "DOLARES AMERICANOS");

  if (["USD", "US", "DOLAR", "DÓLAR", "DOLARES", "DÓLARES"].includes(value)) {
    return "DOLARES AMERICANOS";
  }

  if (["BOB", "BS", "BOLIVIANOS"].includes(value)) {
    return "BOLIVIANOS";
  }

  return value || "DOLARES AMERICANOS";
};

const getCurrencyShort = (currency) => {
  const value = upper(currency, "US");

  if (value.includes("BOLIVIAN") || value === "BOB" || value === "BS")
    return "BS";
  if (value.includes("DOLAR") || value.includes("DÓLAR") || value === "USD")
    return "USD";

  return value;
};

const normalizeItems = (data) =>
  (Array.isArray(data?.itemsOrdenDeCompraSubDatas)
    ? data.itemsOrdenDeCompraSubDatas
    : []
  ).map((item, index) => {
    const detail = safeJson(item?.description2 || item?.Description2);

    const amount = toNumber(
      detail.amount ??
        detail.cantidad ??
        item?.amount ??
        item?.numberValue ??
        item?.NumberValue,
    );

    const unitPrice = toNumber(
      detail.unitPrice ?? detail.precioUnitario ?? item?.unitPrice,
    );

    const discount =
      toNumber(detail.discount ?? detail.descuento ?? item?.discount) ?? 0;

    const savedTotal = toNumber(
      detail.itemTotal ?? detail.total ?? item?.itemTotal ?? item?.total,
    );

    const calculatedTotal =
      amount !== null && unitPrice !== null
        ? amount * unitPrice - discount
        : null;

    return {
      itemNo: optional(
        detail.itemNo ??
          detail.item ??
          item?.numberValue ??
          item?.NumberValue ??
          index + 1,
      ),
      description: optional(
        detail.desc ??
          detail.description ??
          detail.descripcion ??
          item?.label ??
          item?.Label,
      ),
      unit: optional(detail.unit ?? detail.unidad ?? item?.unit),
      amount,
      unitPrice,
      discount,
      total: savedTotal !== null ? savedTotal : calculatedTotal,
      delivery: formatDate(
        detail.delivery ?? detail.fechaEntrega ?? item?.delivery,
        EMPTY_VALUE,
      ),
    };
  });

const normalizeAdditionals = (data) =>
  (Array.isArray(data?.adicionalesDeLaOrdenDeCompraSubDatas)
    ? data.adicionalesDeLaOrdenDeCompraSubDatas
    : []
  )
    .map((item) => {
      const detail = safeJson(item?.description2 || item?.Description2);

      const label = optional(
        item?.label ??
          item?.Label ??
          detail.label ??
          detail.description ??
          detail.descripcion,
      );

      const value = toNumber(
        detail.value ??
          detail.valor ??
          detail.unitPrice ??
          detail.total ??
          item?.value ??
          item?.numberValue,
      );

      return { label, value };
    })
    .filter((row) => row.label !== EMPTY_VALUE || row.value !== null);

const buildPurchaseOrder = (data, provider) => {
  const items = normalizeItems(data);
  const additionals = normalizeAdditionals(data).filter(
    (item) => !isExchangeRateDifference(item),
  );

  const itemsSubtotal = items.reduce(
    (acc, item) => acc + (toNumber(item.total) ?? 0),
    0,
  );

  const subtotal = toNumber(data?.subtotalOrdenDeCompra) ?? itemsSubtotal;
  const additionalsTotal = additionals.reduce(
    (acc, item) => acc + (toNumber(item.value) ?? 0),
    0,
  );
  const total = subtotal + additionalsTotal;

  return {
    date: formatDate(data?.fechaDeOrdenDeCompra),
    ocNumber: required(data?.nroOc),
    scNumber: required(data?.scNro),
    orderCode: required(data?.codigoDeInterno),

    providerName: required(data?.proveedor),
    providerContact: getProviderContact(provider),
    providerNit: required(
      provider?.nit_tax_id || provider?.nit || provider?.nit_ruc,
    ),
    providerPhone: required(
      provider?.telefono || provider?.phone || provider?.celular,
    ),
    providerFax: PENDING_VALUE,
    providerEmail: required(provider?.email || provider?.correo),
    providerAddress: required(provider?.direccion || provider?.address),
    providerLocation: providerLocation(provider),

    paymentTerms: required(data?.terminosPago),
    conditions: required(data?.condiciones),
    observations: required(data?.obsDeLaOrdenDeCompra),

    currencyLabel: getCurrencyLabel(data?.moneda),
    currencyShort: getCurrencyShort(data?.moneda),
    items,
    additionals,
    subtotal,
    total,
  };
};

const buildProviderPurchaseOrder = (data, provider) => {
  const order = buildPurchaseOrder(data, provider);

  const providerAdditionals = order.additionals.filter(
    (item) => !isExchangeRateDifference(item),
  );

  const providerAdditionalsTotal = providerAdditionals.reduce(
    (acc, item) => acc + (toNumber(item.value) ?? 0),
    0,
  );

  return {
    ...order,
    additionals: providerAdditionals,
    total: order.subtotal + providerAdditionalsTotal,
  };
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 44,
    paddingHorizontal: 62,
    paddingBottom: 38,
    fontFamily: "Helvetica",
    fontSize: 6.2,
    color: "#000",
    backgroundColor: "#fff",
  },

  documentBox: {
    borderWidth: 0.8,
    borderColor: "#000",
    borderStyle: "solid",
  },

  row: { flexDirection: "row" },

  borderRight: {
    borderRightWidth: 0.8,
    borderRightColor: "#000",
    borderRightStyle: "solid",
  },

  borderBottom: {
    borderBottomWidth: 0.8,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
  },

  logoBox: {
    width: "19.5%",
    minHeight: 70,
    justifyContent: "center",
    alignItems: "center",
    padding: 6,
  },

  logo: {
    width: 70,
    height: 35,
    objectFit: "contain",
  },

  titleBox: {
    width: "43.5%",
    minHeight: 70,
    alignItems: "center",
    justifyContent: "center",
    paddingTop: 7,
  },

  title: {
    fontSize: 18,
    fontWeight: "bold",
    letterSpacing: 0.2,
    marginBottom: 5,
  },

  subtitle: {
    fontSize: 7.5,
    fontWeight: "bold",
    marginBottom: 8,
  },

  titleMetaRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "flex-end",
    justifyContent: "space-between",
    paddingHorizontal: 5,
  },

  headerCode: {
    fontSize: 5.2,
    fontWeight: "bold",
    lineHeight: 1.15,
  },

  pageNumber: {
    fontSize: 5.5,
    fontWeight: "bold",
  },

  headerInfo: {
    width: "37%",
    minHeight: 70,
  },

  headerInfoRow: {
    flexDirection: "row",
    height: 17.5,
    borderBottomWidth: 0.8,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
    alignItems: "center",
  },

  headerInfoLastRow: {
    borderBottomWidth: 0,
  },

  headerInfoLabel: {
    width: "43%",
    paddingLeft: 5,
    fontSize: 7,
    fontWeight: "bold",
  },

  headerInfoValue: {
    width: "57%",
    textAlign: "center",
    fontSize: 7,
    fontWeight: "bold",
  },

  companyColumn: {
    width: "50%",
    minHeight: 116,
    paddingHorizontal: 4,
    paddingTop: 4,
    paddingBottom: 3,
  },

  providerColumn: {
    width: "50%",
    minHeight: 116,
    paddingHorizontal: 4,
    paddingTop: 4,
    paddingBottom: 3,
  },

  companyTitle: {
    fontSize: 7,
    fontWeight: "bold",
    marginBottom: 5,
  },

  fieldLine: {
    flexDirection: "row",
    marginBottom: 2.4,
    alignItems: "flex-start",
  },

  fieldLabel: {
    fontSize: 6.3,
    fontWeight: "bold",
  },

  fieldValue: {
    fontSize: 6.3,
    flex: 1,
  },

  addressLine: {
    fontSize: 6.3,
    marginTop: 2,
    marginBottom: 7,
  },

  cityLine: {
    fontSize: 6.3,
    marginTop: 3,
  },

  costCenterBox: {
    flexDirection: "row",
    position: "absolute",
    left: 4,
    right: 4,
    bottom: 0,
  },

  costCell: {
    flexDirection: "row",
    width: "33.33%",
  },

  conditionsBox: {
    paddingHorizontal: 4,
    paddingVertical: 2.8,
  },

  conditionLine: {
    flexDirection: "row",
    marginBottom: 2,
  },

  conditionLabel: {
    fontSize: 5.9,
    fontWeight: "bold",
  },

  conditionValue: {
    fontSize: 5.9,
    flex: 1,
  },

  table: {
    width: "100%",
  },

  tableHeaderRow: {
    flexDirection: "row",
    minHeight: 18,
    borderBottomWidth: 0.8,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
  },

  tableRow: {
    flexDirection: "row",
    minHeight: 13,
    borderBottomWidth: 0.8,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
  },

  noBorderBottom: {
    borderBottomWidth: 0,
  },

  tableCell: {
    paddingHorizontal: 2.2,
    paddingVertical: 2.4,
    borderRightWidth: 0.8,
    borderRightColor: "#000",
    borderRightStyle: "solid",
    justifyContent: "center",
  },

  lastCell: {
    borderRightWidth: 0,
  },

  tableHeaderText: {
    fontSize: 5.1,
    fontWeight: "bold",
    textAlign: "center",
    lineHeight: 1.15,
  },

  tableText: {
    fontSize: 5.2,
    lineHeight: 1.25,
  },

  tableTextBold: {
    fontSize: 5.2,
    fontWeight: "bold",
    lineHeight: 1.25,
  },

  textCenter: {
    textAlign: "center",
  },

  textRight: {
    textAlign: "right",
  },

  colItem: {
    width: "4.5%",
  },

  colDescription: {
    width: "36.5%",
  },

  colUnit: {
    width: "6.5%",
  },

  colAmount: {
    width: "10.5%",
  },

  colUnitPrice: {
    width: "10.5%",
  },

  colDiscount: {
    width: "8.5%",
  },

  colTotal: {
    width: "10.5%",
  },

  colDelivery: {
    width: "12.5%",
  },

  totalLabelColumn: {
    width: "41%",
  },

  providerFooter: {
    marginTop: 155,
  },

  providerChecklistTitle: {
    fontSize: 5.4,
    marginBottom: 2,
  },

  providerChecklistRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 1.2,
  },

  pdfCheckbox: {
    width: 6.2,
    height: 6.2,
    borderWidth: 0.8,
    borderColor: "#000",
    marginRight: 3,
    alignItems: "center",
    justifyContent: "center",
  },

  providerChecklistText: {
    fontSize: 5.2,
  },

  providerLegalNote: {
    fontSize: 4.8,
    lineHeight: 1.15,
    marginTop: 8,
  },

  signatureTable: {
    marginTop: 48,
    borderWidth: 0.8,
    borderColor: "#000",
    borderStyle: "solid",
  },

  signatureHeader: {
    flexDirection: "row",
    height: 13,
    borderBottomWidth: 0.8,
    borderBottomColor: "#000",
    borderBottomStyle: "solid",
  },

  signatureBody: {
    flexDirection: "row",
    height: 58,
  },

  signatureCell: {
    width: "25%",
    borderRightWidth: 0.8,
    borderRightColor: "#000",
    borderRightStyle: "solid",
    paddingHorizontal: 3,
    justifyContent: "center",
  },

  signatureBodyCell: {
    width: "25%",
    borderRightWidth: 0.8,
    borderRightColor: "#000",
    borderRightStyle: "solid",
    paddingHorizontal: 3,
    justifyContent: "flex-end",
    paddingBottom: 5,
  },

  signatureLastCell: {
    borderRightWidth: 0,
  },

  signatureHeaderText: {
    fontSize: 5.6,
    fontWeight: "bold",
  },

  signatureBodyText: {
    fontSize: 5.2,
  },
});

const HeaderInfoRow = ({ label, value, isLast = false }) => (
  <View style={[styles.headerInfoRow, isLast && styles.headerInfoLastRow]}>
    <Text style={styles.headerInfoLabel}>{label}</Text>
    <Text style={styles.headerInfoValue}>{value}</Text>
  </View>
);

const FieldLine = ({ label, value, labelWidth = 31, boldValue = false }) => (
  <View style={styles.fieldLine}>
    <Text style={[styles.fieldLabel, { width: labelWidth }]}>{label}</Text>
    <Text style={[styles.fieldValue, boldValue && { fontWeight: "bold" }]}>
      {value}
    </Text>
  </View>
);

const TableRow = ({ children, style }) => (
  <View wrap={false} style={[styles.tableRow, style]}>
    {children}
  </View>
);

const Cell = ({ children, style, textStyle, isLast = false }) => (
  <View style={[styles.tableCell, style, isLast && styles.lastCell]}>
    <Text style={textStyle || styles.tableText}>{children}</Text>
  </View>
);

const PdfCheckbox = ({ checked }) => (
  <View style={styles.pdfCheckbox}>
    {checked ? (
      <Svg width={5.5} height={5.5} viewBox="0 0 10 10">
        <Line x1="1" y1="1" x2="9" y2="9" stroke="#000000" strokeWidth={1.8} />
        <Line x1="9" y1="1" x2="1" y2="9" stroke="#000000" strokeWidth={1.8} />
      </Svg>
    ) : null}
  </View>
);

const ProviderDocumentsChecklist = ({ documentChecks, language = "es" }) => {
  const t = (key) => translations[language]?.[key] || key;

  return (
    <View wrap={false}>
      <Text style={styles.providerChecklistTitle}>{t("documentsRequest")}</Text>

      {PROVIDER_PURCHASE_ORDER_DOCUMENTS.map((document) => (
        <View key={document.key} style={styles.providerChecklistRow}>
          <PdfCheckbox checked={Boolean(documentChecks?.[document.key])} />
          <Text style={styles.providerChecklistText}>{document.label}</Text>
        </View>
      ))}
    </View>
  );
};

const ProviderPurchaseOrderFooter = ({ documentChecks, language = "es" }) => {
  const t = (key) => translations[language]?.[key] || key;

  return (
    <View style={styles.providerFooter} wrap={false}>
      <ProviderDocumentsChecklist
        documentChecks={documentChecks}
        language={language}
      />

      <Text style={styles.providerLegalNote}>
        <Text style={{ fontWeight: "bold" }}>{t("note")}</Text>
        {t("noteText")}
      </Text>

      <View style={styles.signatureTable}>
        <View style={styles.signatureHeader}>
          <View style={styles.signatureCell}>
            <Text style={styles.signatureHeaderText}>{t("approvedBy")}</Text>
          </View>

          <View style={styles.signatureCell}>
            <Text style={styles.signatureHeaderText}>{EMPTY_VALUE}</Text>
          </View>

          <View style={styles.signatureCell}>
            <Text style={styles.signatureHeaderText}>{EMPTY_VALUE}</Text>
          </View>

          <View style={[styles.signatureCell, styles.signatureLastCell]}>
            <Text style={styles.signatureHeaderText}>
              {t("providerApproval")}
            </Text>
          </View>
        </View>

        <View style={styles.signatureBody}>
          <View style={styles.signatureBodyCell}>
            <Image
              src={"/images/firma-representante-plastiforte.png"}
              style={styles.signatureBodyText}
            />
            <Text style={styles.signatureBodyText}>
              Gabriela Miranda Garcia
            </Text>
          </View>

          <View style={styles.signatureBodyCell}>
            <Text style={styles.signatureBodyText}>{EMPTY_VALUE}</Text>
          </View>

          <View style={styles.signatureBodyCell}>
            <Text style={styles.signatureBodyText}>{EMPTY_VALUE}</Text>
          </View>

          <View style={[styles.signatureBodyCell, styles.signatureLastCell]}>
            <Text style={styles.signatureBodyText}>{EMPTY_VALUE}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const ExportPurchaseOrderPDF = ({
  data,
  providerDetails,
  order: orderProp,
  isProvider = false,
  documentChecks = DEFAULT_PROVIDER_PURCHASE_ORDER_DOCUMENT_CHECKS,
  renderKey,
  language = "es",
}) => {
  const t = (key) => translations[language]?.[key] || key;

  if (!data && !orderProp) return null;

  const order = orderProp || buildPurchaseOrder(data, providerDetails);

  return (
    <Document key={renderKey}>
      <Page size="LETTER" style={styles.page} wrap>
        <View style={styles.documentBox}>
          <View style={[styles.row, styles.borderBottom]}>
            <View style={[styles.logoBox, styles.borderRight]}>
              <Image src={LOGO_PATH} style={styles.logo} />
            </View>

            <View style={[styles.titleBox, styles.borderRight]}>
              <Text style={styles.title}>{t("purchaseOrderTitle")}</Text>
              <Text style={styles.subtitle}>{t("purchaseOrderSubtitle")}</Text>

              <View style={styles.titleMetaRow}>
                <Text style={styles.headerCode}>
                  {t("codigo")} PR-ADQ-142-R266{"\n"}
                  {t("version")} 6
                </Text>
                <Text style={styles.pageNumber}>
                  {t("page")} 1 {t("of")} 1
                </Text>
              </View>
            </View>

            <View style={styles.headerInfo}>
              <HeaderInfoRow label={t("date")} value={order.date} />
              <HeaderInfoRow label={t("ocNumber")} value={order.ocNumber} />
              <HeaderInfoRow label={t("scNumber")} value={order.scNumber} />
              <HeaderInfoRow
                label={t("orderCode")}
                value={order.orderCode}
                isLast
              />
            </View>
          </View>

          <View style={[styles.row, styles.borderBottom]}>
            <View style={[styles.companyColumn, styles.borderRight]}>
              <Text style={styles.companyTitle}>
                {isProvider ? t("plastiforteLong") : t("plastiforteShort")}
              </Text>

              <FieldLine label={t("att")} value="Gabriela Miranda Garcia" />
              <FieldLine label={t("nit")} value="1009109025" />

              <View style={styles.fieldLine}>
                <Text style={[styles.fieldLabel, { width: 41 }]}>
                  {t("phone")}
                </Text>
                <Text style={[styles.fieldValue, { width: 80, flex: 0 }]}>
                  {isProvider ? "+591 78355252" : "78355252"}
                </Text>
                <Text style={[styles.fieldLabel, { left: 50 }]}>
                  {isProvider ? t("phone") : t("fax")}
                </Text>
                <Text style={[styles.fieldValue, { left: 70 }]}>
                  {isProvider ? "+591 4 4433270" : "4433270"}
                </Text>
              </View>

              <FieldLine
                label={t("email")}
                value="gabriela.miranda@plastiforte.com"
                labelWidth={41}
              />

              <Text style={styles.addressLine}>
                Av. Blanco Galindo No. 3011 esq. Arthur Ponsnanski
              </Text>

              <Text style={styles.cityLine}>
                {isProvider ? "COCHABAMBA - BOLIVIA" : "COCHABAMBA"}
              </Text>

              {!isProvider && (
                <View style={styles.costCenterBox}>
                  <View style={styles.costCell}>
                    <Text style={styles.fieldLabel}>{t("cc")} </Text>
                    <Text style={styles.fieldValue}>4301</Text>
                  </View>

                  <View style={styles.costCell}>
                    <Text style={styles.fieldLabel}>{t("ca")} </Text>
                    <Text style={styles.fieldValue}>{EMPTY_VALUE}</Text>
                  </View>

                  <View style={styles.costCell}>
                    <Text style={styles.fieldValue}>{EMPTY_VALUE}</Text>
                  </View>
                </View>
              )}
            </View>

            <View style={styles.providerColumn}>
              {isProvider ? (
                <>
                  <FieldLine
                    label={t("provider")}
                    value={upper(order.providerName, PENDING_VALUE)}
                    labelWidth={48}
                    boldValue
                  />

                  <FieldLine
                    label={t("providerContact")}
                    value={order.providerContact}
                    labelWidth={26}
                  />

                  <FieldLine
                    label={t("quoteNumber")}
                    value={EMPTY_VALUE}
                    labelWidth={54}
                  />

                  <FieldLine
                    label={t("address")}
                    value={order.providerAddress}
                    labelWidth={44}
                  />

                  <Text style={styles.cityLine}>{order.providerLocation}</Text>
                </>
              ) : (
                <>
                  <FieldLine
                    label={t("for")}
                    value={upper(order.providerName, PENDING_VALUE)}
                    labelWidth={25}
                    boldValue
                  />

                  <FieldLine
                    label={t("providerContact")}
                    value={order.providerContact}
                    labelWidth={26}
                  />

                  <View style={styles.fieldLine}>
                    <Text style={[styles.fieldValue, { width: 86, flex: 0 }]}>
                      {EMPTY_VALUE}
                    </Text>
                    <Text style={[styles.fieldLabel, { width: 20 }]}>
                      {t("nit")}
                    </Text>
                    <Text style={styles.fieldValue}>{order.providerNit}</Text>
                  </View>

                  <View style={styles.fieldLine}>
                    <Text style={[styles.fieldLabel, { width: 26 }]}>
                      {t("phoneShort")}
                    </Text>
                    <Text style={styles.fieldValue}>{order.providerPhone}</Text>
                  </View>

                  <FieldLine
                    label={t("email")}
                    value={order.providerEmail}
                    labelWidth={35}
                  />

                  <Text style={styles.addressLine}>
                    {order.providerAddress}
                  </Text>
                  <Text style={styles.cityLine}>{order.providerLocation}</Text>
                </>
              )}
            </View>
          </View>

          <View style={[styles.conditionsBox, styles.borderBottom]}>
            <View style={styles.conditionLine}>
              <Text style={styles.conditionLabel}>{t("paymentTerms")} </Text>
              <Text style={styles.conditionValue}>
                {upper(order.paymentTerms, PENDING_VALUE)}
              </Text>
            </View>

            <View style={styles.conditionLine}>
              <Text style={styles.conditionLabel}>
                {isProvider ? `${t("incoterms")} ` : `${t("conditions")} `}
              </Text>
              <Text style={styles.conditionValue}>
                {upper(order.conditions, PENDING_VALUE)}
              </Text>
            </View>

            {/* <View style={styles.conditionLine}>
              <Text style={styles.conditionLabel}>{t("observations")} </Text>
              <Text style={styles.conditionValue}>
                {upper(order.observations, PENDING_VALUE)}
              </Text>
            </View> */}
          </View>

          <View style={styles.table}>
            <TableRow style={styles.tableHeaderRow}>
              <Cell style={styles.colItem} textStyle={styles.tableHeaderText}>
                {t("item")}
              </Cell>

              <Cell
                style={styles.colDescription}
                textStyle={styles.tableHeaderText}
              >
                {t("description")}
              </Cell>

              <Cell style={styles.colUnit} textStyle={styles.tableHeaderText}>
                {t("unit")}
              </Cell>

              <Cell style={styles.colAmount} textStyle={styles.tableHeaderText}>
                {t("quantity")}
              </Cell>

              <Cell
                style={styles.colUnitPrice}
                textStyle={styles.tableHeaderText}
              >
                {t("unitPrice")}
              </Cell>

              <Cell
                style={styles.colDiscount}
                textStyle={styles.tableHeaderText}
              >
                {t("discount")}
              </Cell>

              <Cell style={styles.colTotal} textStyle={styles.tableHeaderText}>
                {t("total")} {order.currencyShort}
              </Cell>

              <Cell
                style={styles.colDelivery}
                textStyle={styles.tableHeaderText}
                isLast
              >
                {t("deliveryDate")}
              </Cell>
            </TableRow>

            {order.items.length > 0 ? (
              order.items.map((item, index) => (
                <TableRow key={`${item.itemNo}-${index}`}>
                  <Cell
                    style={styles.colItem}
                    textStyle={[styles.tableText, styles.textCenter]}
                  >
                    {item.itemNo}
                  </Cell>

                  <Cell
                    style={styles.colDescription}
                    textStyle={styles.tableTextBold}
                  >
                    {upper(item.description)}
                  </Cell>

                  <Cell
                    style={styles.colUnit}
                    textStyle={[styles.tableText, styles.textCenter]}
                  >
                    {upper(item.unit)}
                  </Cell>

                  <Cell
                    style={styles.colAmount}
                    textStyle={[styles.tableText, styles.textRight]}
                  >
                    {money(item.amount)}
                  </Cell>

                  <Cell
                    style={styles.colUnitPrice}
                    textStyle={[styles.tableText, styles.textRight]}
                  >
                    {money(item.unitPrice)}
                  </Cell>

                  <Cell
                    style={styles.colDiscount}
                    textStyle={[styles.tableText, styles.textRight]}
                  >
                    {money(item.discount)}
                  </Cell>

                  <Cell
                    style={styles.colTotal}
                    textStyle={[styles.tableTextBold, styles.textRight]}
                  >
                    {money(item.total)}
                  </Cell>

                  <Cell
                    style={styles.colDelivery}
                    textStyle={[styles.tableText, styles.textCenter]}
                    isLast
                  >
                    {item.delivery}
                  </Cell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <Cell
                  style={styles.colItem}
                  textStyle={[styles.tableText, styles.textCenter]}
                >
                  1
                </Cell>
                <Cell style={styles.colDescription}>{EMPTY_VALUE}</Cell>
                <Cell
                  style={styles.colUnit}
                  textStyle={[styles.tableText, styles.textCenter]}
                >
                  {EMPTY_VALUE}
                </Cell>
                <Cell
                  style={styles.colAmount}
                  textStyle={[styles.tableText, styles.textRight]}
                >
                  {EMPTY_VALUE}
                </Cell>
                <Cell
                  style={styles.colUnitPrice}
                  textStyle={[styles.tableText, styles.textRight]}
                >
                  {EMPTY_VALUE}
                </Cell>
                <Cell
                  style={styles.colDiscount}
                  textStyle={[styles.tableText, styles.textRight]}
                >
                  {EMPTY_VALUE}
                </Cell>
                <Cell
                  style={styles.colTotal}
                  textStyle={[styles.tableText, styles.textRight]}
                >
                  {EMPTY_VALUE}
                </Cell>
                <Cell
                  style={styles.colDelivery}
                  textStyle={[styles.tableText, styles.textCenter]}
                  isLast
                >
                  {EMPTY_VALUE}
                </Cell>
              </TableRow>
            )}

            <TableRow>
              <Cell
                style={styles.totalLabelColumn}
                textStyle={styles.tableTextBold}
              >
                {isProvider ? t("totalLabel") : t("subtotal")}
              </Cell>
              <Cell style={styles.colUnit}>{EMPTY_VALUE}</Cell>
              <Cell style={styles.colAmount}>{EMPTY_VALUE}</Cell>
              <Cell style={styles.colUnitPrice}>{EMPTY_VALUE}</Cell>
              <Cell
                style={styles.colDiscount}
                textStyle={[styles.tableText, styles.textRight]}
              >
                0.00
              </Cell>
              <Cell
                style={styles.colTotal}
                textStyle={[styles.tableTextBold, styles.textRight]}
              >
                {money(order.subtotal, PENDING_VALUE)}
              </Cell>
              <Cell style={styles.colDelivery} isLast>
                {EMPTY_VALUE}
              </Cell>
            </TableRow>

            {order.additionals.map((item, index) => (
              <TableRow key={`${item.label}-${index}`}>
                <Cell
                  style={styles.totalLabelColumn}
                  textStyle={styles.tableTextBold}
                >
                  {upper(item.label)}
                </Cell>

                <Cell style={styles.colUnit}>{EMPTY_VALUE}</Cell>
                <Cell style={styles.colAmount}>{EMPTY_VALUE}</Cell>
                <Cell style={styles.colUnitPrice}>{EMPTY_VALUE}</Cell>
                <Cell
                  style={styles.colDiscount}
                  textStyle={[styles.tableText, styles.textRight]}
                >
                  0.00
                </Cell>
                <Cell
                  style={styles.colTotal}
                  textStyle={[styles.tableTextBold, styles.textRight]}
                >
                  {money(item.value, PENDING_VALUE)}
                </Cell>
                <Cell style={styles.colDelivery} isLast>
                  {EMPTY_VALUE}
                </Cell>
              </TableRow>
            ))}

            <TableRow style={styles.noBorderBottom}>
              <Cell
                style={styles.totalLabelColumn}
                textStyle={styles.tableTextBold}
              >
                {t("purchaseOrderIn")} {order.currencyLabel}
              </Cell>

              <Cell style={styles.colUnit}>{EMPTY_VALUE}</Cell>
              <Cell style={styles.colAmount}>{EMPTY_VALUE}</Cell>
              <Cell style={styles.colUnitPrice}>{EMPTY_VALUE}</Cell>
              <Cell
                style={styles.colDiscount}
                textStyle={[styles.tableText, styles.textRight]}
              >
                0.00
              </Cell>
              <Cell
                style={styles.colTotal}
                textStyle={[styles.tableTextBold, styles.textRight]}
              >
                {money(order.total, PENDING_VALUE)}
              </Cell>
              <Cell style={styles.colDelivery} isLast>
                {EMPTY_VALUE}
              </Cell>
            </TableRow>
          </View>
        </View>

        {isProvider && (
          <ProviderPurchaseOrderFooter
            documentChecks={documentChecks}
            language={language}
          />
        )}
      </Page>
    </Document>
  );
};

const ProviderDocumentsModal = ({
  initialChecks,
  onCancel,
  onConfirm,
  language = "es",
}) => {
  const t = (key) => translations[language]?.[key] || key;
  const [checks, setChecks] = useState({
    ...DEFAULT_PROVIDER_PURCHASE_ORDER_DOCUMENT_CHECKS,
    ...(initialChecks || {}),
  });

  const allSelected = PROVIDER_PURCHASE_ORDER_DOCUMENTS.every(
    (document) => checks[document.key],
  );

  const handleToggle = (key) => {
    setChecks((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSelectAll = () => {
    const nextValue = !allSelected;

    const nextChecks = PROVIDER_PURCHASE_ORDER_DOCUMENTS.reduce(
      (acc, document) => {
        acc[document.key] = nextValue;
        return acc;
      },
      {},
    );

    setChecks(nextChecks);
  };

  const handleClear = () => {
    setChecks(DEFAULT_PROVIDER_PURCHASE_ORDER_DOCUMENT_CHECKS);
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
          <h3 className="text-lg font-bold text-gray-800">
            {t("docsModalTitle")}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {t("docsModalDescription")}
          </p>
        </div>

        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-xs font-bold text-blue-700 hover:text-blue-900"
            >
              {allSelected ? t("deselectAll") : t("selectAll")}
            </button>

            <button
              type="button"
              onClick={handleClear}
              className="text-xs font-bold text-gray-500 hover:text-gray-800"
            >
              {t("clear")}
            </button>
          </div>

          <div className="space-y-2">
            {PROVIDER_PURCHASE_ORDER_DOCUMENTS.map((document) => (
              <label
                key={document.key}
                className="flex items-center gap-3 p-2 rounded-md border border-gray-200 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={Boolean(checks[document.key])}
                  onChange={() => handleToggle(document.key)}
                  className="w-4 h-4"
                />

                <span className="text-sm font-medium text-gray-700">
                  {document.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-bold text-gray-600 hover:bg-gray-100"
          >
            {t("cancel")}
          </button>

          <button
            type="button"
            onClick={() => onConfirm({ ...checks })}
            className="px-4 py-2 rounded-md bg-blue-700 text-white text-sm font-bold hover:bg-blue-800"
          >
            {t("generateProviderPdf")}
          </button>
        </div>
      </div>
    </div>
  );
};

export const PurchaseOrder = ({ procedureId }) => {
  const [language, setLanguage] = useState(LANGUAGES.ES);
  const t = React.useCallback(
    (key) => translations[language]?.[key] || key,
    [language],
  );
  const [providerDetails, setProviderDetails] = useState(null);
  const [providerLoading, setProviderLoading] = useState(false);
  const [providerError, setProviderError] = useState(false);
  const [selectedPdfType, setSelectedPdfType] = useState(
    PURCHASE_ORDER_PDF_TYPES.INTERNAL,
  );
  const [showProviderDocsModal, setShowProviderDocsModal] = useState(false);
  const [providerDocumentChecks, setProviderDocumentChecks] = useState(
    normalizeProviderDocumentChecks(
      DEFAULT_PROVIDER_PURCHASE_ORDER_DOCUMENT_CHECKS,
    ),
  );
  const [providerPdfVersion, setProviderPdfVersion] = useState(0);

  const { data: mergedData, loading: isLoaded } = Request({
    urlApi: `/Procedure/${procedureId}/dataValues?${buildFieldQuery()}`,
  });

  const providerName = useMemo(
    () => getPurchaseOrderProviderName(mergedData),
    [mergedData],
  );

  const purchaseOrderNumber =
    mergedData?.nroOc || mergedData?.codigoDeInterno || procedureId;

  const providerPurchaseOrder = useMemo(() => {
    if (!mergedData) return null;

    return buildProviderPurchaseOrder(mergedData, providerDetails);
  }, [mergedData, providerDetails]);

  const isProviderPdf = selectedPdfType === PURCHASE_ORDER_PDF_TYPES.PROVIDER;

  const selectedProviderDocumentsCount =
    PROVIDER_PURCHASE_ORDER_DOCUMENTS.filter(
      (item) => providerDocumentChecks[item.key],
    ).length;

  const providerPdfRenderKey = useMemo(() => {
    const selectedKeys = PROVIDER_PURCHASE_ORDER_DOCUMENTS.filter(
      (item) => providerDocumentChecks[item.key],
    )
      .map((item) => item.key)
      .join("-");

    return `provider-${providerPdfVersion}-${selectedKeys || "none"}`;
  }, [providerDocumentChecks, providerPdfVersion]);

  const pdfButtonLabel = isProviderPdf
    ? t("downloadProviderPdf")
    : t("downloadOrder");

  const pdfFileSuffix = isProviderPdf
    ? t("pdfSuffixProvider")
    : t("pdfSuffixInternal");

  const currentPdfDocument = isProviderPdf ? (
    <ExportPurchaseOrderPDF
      key={providerPdfRenderKey}
      renderKey={providerPdfRenderKey}
      order={providerPurchaseOrder}
      isProvider
      documentChecks={providerDocumentChecks}
      language={language}
    />
  ) : (
    <ExportPurchaseOrderPDF
      key="internal-purchase-order"
      renderKey="internal-purchase-order"
      data={mergedData}
      providerDetails={providerDetails}
      language={language}
    />
  );

  useEffect(() => {
    let isMounted = true;

    const fetchProvider = async () => {
      if (!isLoaded || !providerName) {
        setProviderDetails(null);
        setProviderError(false);
        return;
      }

      setProviderLoading(true);
      setProviderError(false);

      try {
        const providerSearchTerms = getProviderSearchTerms(providerName);
        let matchingProvider = null;

        for (const searchParam of ["name", "Name"]) {
          for (const term of providerSearchTerms) {
            const encodedName = encodeURIComponent(term);

            const response = await requestAuth(
              "get",
              `/Dropdown/providers/options?PageNumber=1&${searchParam}=${encodedName}`,
            );

            matchingProvider = findMatchingProvider(
              getArray(response.data),
              providerName,
            );

            if (matchingProvider) break;
          }

          if (matchingProvider) break;
        }

        if (!isMounted) return;

        setProviderDetails(matchingProvider);
        setProviderError(!matchingProvider);
      } catch {
        if (!isMounted) return;

        setProviderDetails(null);
        setProviderError(true);
      } finally {
        if (isMounted) setProviderLoading(false);
      }
    };

    fetchProvider();

    return () => {
      isMounted = false;
    };
  }, [isLoaded, providerName]);

  const handleOpenProviderPdfModal = () => {
    setShowProviderDocsModal(true);
  };

  const handleConfirmProviderDocuments = (documentChecks) => {
    const normalizedChecks = normalizeProviderDocumentChecks(documentChecks);

    setProviderDocumentChecks(normalizedChecks);
    setSelectedPdfType(PURCHASE_ORDER_PDF_TYPES.PROVIDER);
    setProviderPdfVersion((currentVersion) => currentVersion + 1);
    setShowProviderDocsModal(false);
  };

  return (
    <div className="flex flex-col w-[80vw] bg-white rounded-xl shadow-2xl overflow-hidden h-[85vh]">
      {showProviderDocsModal && (
        <ProviderDocumentsModal
          initialChecks={providerDocumentChecks}
          onCancel={() => setShowProviderDocsModal(false)}
          onConfirm={handleConfirmProviderDocuments}
          language={language}
        />
      )}

      <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="flex flex-col">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <i className="fas fa-file-invoice text-blue-700"></i>
            {t("orderOf")}
          </h2>

          <span className="text-xs text-gray-500 font-medium">
            {purchaseOrderNumber
              ? `${t("nro")} ${purchaseOrderNumber}`
              : `ID: ${procedureId}`}
          </span>

          {providerLoading && (
            <span className="text-[11px] text-blue-600 font-medium mt-1">
              {t("completingProvider")}
            </span>
          )}

          {!providerLoading && providerError && providerName && (
            <span className="text-[11px] text-amber-600 font-medium mt-1">
              {t("providerNotFound")}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          {!isLoaded ? (
            <span className="text-blue-600 text-sm font-semibold flex items-center gap-3 bg-blue-50 px-4 py-2 rounded-full">
              <Circle /> {t("loadingData")}
            </span>
          ) : (
            <>
              {isProviderPdf && (
                <span className="text-xs font-semibold text-blue-700 bg-blue-50 border border-blue-100 px-3 py-2 rounded-md">
                  {selectedProviderDocumentsCount} {t("documentsChecked")}
                </span>
              )}

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setSelectedPdfType(PURCHASE_ORDER_PDF_TYPES.INTERNAL)
                  }
                  className={`px-3 py-2 rounded-md text-xs font-bold border transition ${
                    !isProviderPdf
                      ? "bg-blue-700 text-white border-blue-700"
                      : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {t("pdfInternal")}
                </button>

                <button
                  type="button"
                  onClick={handleOpenProviderPdfModal}
                  className={`px-3 py-2 rounded-md text-xs font-bold border transition ${
                    isProviderPdf
                      ? "bg-blue-700 text-white border-blue-700"
                      : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {isProviderPdf ? t("changeDocuments") : t("pdfProvider")}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    setLanguage((prev) =>
                      prev === LANGUAGES.ES ? LANGUAGES.EN : LANGUAGES.ES,
                    )
                  }
                  className={`px-3 py-2 rounded-md text-xs font-bold border transition ${
                    language === LANGUAGES.EN
                      ? "bg-emerald-600 text-white border-emerald-600"
                      : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  <i className="fas fa-language mr-1"></i>
                  {language === LANGUAGES.ES
                    ? t("printInEnglish")
                    : t("printInSpanish")}
                </button>
              </div>

              <PDFDownloadLink
                key={`download-${isProviderPdf ? providerPdfRenderKey : "internal"}-${language}`}
                document={currentPdfDocument}
                fileName={`${t("orderOf").replace(/\s+/g, "_")}_${purchaseOrderNumber}_${pdfFileSuffix}.pdf`}
              >
                {({ loading: pdfLoading }) => (
                  <Button
                    className="py-2 px-6 text-sm font-bold flex items-center gap-2 shadow-sm transition-all hover:shadow-md"
                    disabled={pdfLoading}
                  >
                    <i className="fas fa-download"></i>
                    {pdfLoading ? t("generating") : pdfButtonLabel}
                  </Button>
                )}
              </PDFDownloadLink>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 bg-gray-100 p-6 flex flex-col">
        {isLoaded ? (
          <div className="flex-1 bg-white rounded-lg shadow-inner overflow-hidden border border-gray-200">
            <PDFViewer
              key={`viewer-${isProviderPdf ? providerPdfRenderKey : "internal"}`}
              width="100%"
              height="100%"
              showToolbar
              className="border-none"
            >
              {currentPdfDocument}
            </PDFViewer>
          </div>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 bg-white rounded-lg border-2 border-dashed border-gray-200">
            <div className="scale-150">
              <Circle />
            </div>

            <p className="text-gray-400 font-medium animate-pulse">
              {t("syncing")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrder;
